import type { CappedConversationsMap, CappedConversationState, CappedMessageIdArg, ConversationIdArg } from "./types";

/**
 * LocalStorage utility for tracking capped conversation state.
 * Prevents repeated "first time" experiences (pulse, CTA) on refresh/navigation.
 *
 * Uses LRU eviction to prevent unbounded growth.
 * Note: We use localStorage directly (not typedLocalStorageService) because
 * conversations are already user-specific and we need consistent key access
 * before/after user profile loads.
 */

const STORAGE_KEY = "capped_conversations";

const MAX_STORED_CONVERSATIONS = 200;

// ============================================================================
// Internal helpers (optimized for performance)
// ============================================================================

let cachedMap: CappedConversationsMap | null = null;

function getConversationsMap(): CappedConversationsMap {
  if (cachedMap !== null) {
    return cachedMap;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    cachedMap = JSON.parse(stored ?? "{}") as CappedConversationsMap;
  } catch {
    cachedMap = {};
  }

  return cachedMap;
}

function setConversationsMap(map: CappedConversationsMap): void {
  cachedMap = map;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

function pruneOldEntries(map: CappedConversationsMap): CappedConversationsMap {
  const entries = Object.entries(map);

  if (entries.length <= MAX_STORED_CONVERSATIONS) {
    return map;
  }

  // Sort by lastUpdatedAt ascending (oldest first)
  entries.sort((entryA, entryB) => entryA[1].lastUpdatedAt - entryB[1].lastUpdatedAt);

  // Keep only the most recent entries
  const toKeep = entries.slice(entries.length - MAX_STORED_CONVERSATIONS);

  return Object.fromEntries(toKeep);
}

function updateEntry(conversationId: string, updates: Partial<CappedConversationState>): void {
  const map = getConversationsMap();
  const existing = map[conversationId];
  const now = Date.now();

  map[conversationId] = {
    lastCappedMessageId: existing?.lastCappedMessageId ?? "",
    acknowledgedAt: existing?.acknowledgedAt ?? 0,
    pulseShown: existing?.pulseShown ?? false,
    floatingCTADismissed: existing?.floatingCTADismissed ?? false,
    floatingCTALastShownAt: existing?.floatingCTALastShownAt ?? 0,
    indicatorHidden: existing?.indicatorHidden ?? false,
    lastUpdatedAt: now,
    ...updates,
  };

  const prunedMap = pruneOldEntries(map);
  setConversationsMap(prunedMap);
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Initialize capped state for a conversation if it doesn't exist.
 * Call this immediately when a conversation becomes capped to ensure persistence.
 */
export function initCappedState({ conversationId, lastCappedMessageId }: CappedMessageIdArg): void {
  const map = getConversationsMap();
  if (!map[conversationId]) {
    updateEntry(conversationId, { lastCappedMessageId });
  }
}

/**
 * Get the capped state for a conversation.
 * Read-only operation - does not modify storage.
 */
export function getCappedState({ conversationId }: ConversationIdArg): CappedConversationState | null {
  const map = getConversationsMap();
  return map[conversationId] ?? null;
}

/**
 * Mark conversation as acknowledged (user clicked/interacted with indicator).
 */
export function markCappedAcknowledged({ conversationId, lastCappedMessageId }: CappedMessageIdArg): void {
  updateEntry(conversationId, {
    lastCappedMessageId,
    acknowledgedAt: Date.now(),
  });
}

/**
 * Check if pulse animation should show.
 * Returns true if:
 * - No stored state for this conversation, OR
 * - The cappedMessageId changed (more messages truncated)
 */
export function shouldShowPulse({ conversationId, lastCappedMessageId }: CappedMessageIdArg): boolean {
  const map = getConversationsMap();
  const state = map[conversationId];

  if (!state) {
    return true;
  }

  // If capped message ID changed, show pulse again
  if (state.lastCappedMessageId !== lastCappedMessageId) {
    return true;
  }

  return !state.pulseShown;
}

/**
 * Mark pulse as shown for a conversation.
 */
export function markPulseShown({ conversationId, lastCappedMessageId }: CappedMessageIdArg): void {
  updateEntry(conversationId, {
    lastCappedMessageId,
    pulseShown: true,
  });
}

/**
 * Check if floating CTA should show.
 * Returns true if:
 * - No stored state for this conversation, OR
 * - The cappedMessageId changed (more messages truncated), OR
 * - CTA was not previously dismissed AND 24 hours have passed since last shown
 */
export function shouldShowFloatingCTA({ conversationId, lastCappedMessageId }: CappedMessageIdArg): boolean {
  const map = getConversationsMap();
  const state = map[conversationId];

  if (!state) {
    return true;
  }

  // If capped message ID changed, can show CTA again
  if (state.lastCappedMessageId !== lastCappedMessageId) {
    return true;
  }

  // If dismissed permanently, don't show
  if (state.floatingCTADismissed) {
    return false;
  }

  // Check 24-hour cooldown
  const HOURS_IN_DAY = 24;
  const MINUTES_IN_HOUR = 60;
  const SECONDS_IN_MINUTE = 60;
  const MS_IN_SECOND = 1000;
  const COOLDOWN_MS = HOURS_IN_DAY * MINUTES_IN_HOUR * SECONDS_IN_MINUTE * MS_IN_SECOND;
  const timeSinceLastShown = Date.now() - state.floatingCTALastShownAt;
  return timeSinceLastShown >= COOLDOWN_MS;
}

/**
 * Mark floating CTA as shown (for cooldown tracking).
 */
export function markFloatingCTAShown({ conversationId, lastCappedMessageId }: CappedMessageIdArg): void {
  updateEntry(conversationId, {
    lastCappedMessageId,
    floatingCTALastShownAt: Date.now(),
  });
}

/**
 * Mark floating CTA as dismissed permanently for a conversation.
 */
export function markFloatingCTADismissed({ conversationId, lastCappedMessageId }: CappedMessageIdArg): void {
  updateEntry(conversationId, {
    lastCappedMessageId,
    floatingCTADismissed: true,
  });
}

/**
 * Mark indicator as hidden for a conversation (permanent hide).
 */
export function markIndicatorHidden({ conversationId }: ConversationIdArg): void {
  updateEntry(conversationId, {
    indicatorHidden: true,
  });
}

/**
 * Check if indicator is hidden for a conversation.
 */
export function isIndicatorHidden({ conversationId }: ConversationIdArg): boolean {
  const map = getConversationsMap();
  return map[conversationId]?.indicatorHidden ?? false;
}

/**
 * Clear the in-memory cache. Useful for testing.
 */
export function clearCache(): void {
  cachedMap = null;
}
